<template>
  <div
    v-clickoutside="hideSuggestions"
    class="search-field"
  >
    <form
      ref="searchForm"
      role="search"
      @submit="submit"
    >
      <div class="form-input-wrapper">
        <input
          :id="fieldId"
          v-model="searchValue"
          v-tag:useractionevent="{
            action: 'start search',
            category: 'internal search',
            click_action: 'start_search',
            ecommerce: undefined,
            event: 'uaevent',
            event_name: 'internal_search_click',
            label: locationLabel,
          }"
          class="form__input"
          name="search-term"
          type="search"
          autocomplete="off"
          :aria-controls="`list-${fieldId}`"
          autocapitalize="none"
          aria-autocomplete="list"
          role="combobox"
          aria-label="Search"
          :aria-expanded="showSuggestions ? 'true' : 'false'"
          :placeholder="searchPlaceholder"
          @focus="showIfItHasSuggestions"
          @keyup="throttleCheckTerm"
          @keydown.enter="submit"
          @keydown.40="focusFirstSuggestion"
        >
        <button
          v-if="searchValue"
          :aria-label="resetLabel"
          class="search-field__empty"
          @click.prevent="clear"
        >
          <span />
        </button>
        <button
          :aria-label="submitLabel"
          class="search-field__submit"
          @click.prevent="submit"
        >
          <slot />
          <span>{{ submitLabel }}</span>
        </button>
        <label
          for="search-field-input"
          class="form__label is-sr-only"
        >
          <span>{{ submitLabel }}</span>
        </label>
      </div>
    </form>
    <search-suggestions
      v-if="showSuggestions"
      :id="fieldId"
      :article-list-label="articleListLabel"
      :loading="loading"
      :product-list-label="productListLabel"
      :query-suggestion-alternative-text="querySuggestionAlternativeText"
      :suggested="suggested"
      :suggested-articles="suggestedArticles"
      :suggested-clp="suggestedClp"
      :suggested-products="suggestedProducts"
      :term="searchValue"
    />
  </div>
</template>

<script>
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';
  import { tag } from '@Foundation/analyticsHandler/directives/tag';
  import SearchSuggestions from '@Feature/Search/code/Scripts/components/SearchSuggestions/SearchSuggestions.vue';
  import { clickoutside } from '@Foundation/directives/clickoutside/clickoutside';
  import { mapGetters, mapActions } from 'vuex';
  import { throttle } from '@Foundation/utilities';

  export default {
    name: 'SearchField',
    directives: {
      clickoutside,
      tag,
    },
    components: {
      SearchSuggestions,
    },
    props: {
      submitLabel: { type: String, required: false, default: 'Search' },
      locationLabel: { type: String, required: false, default: 'Header' },
      resetLabel: { type: String, required: true },
      querySuggestionAlternativeText: { type: String, required: true },
      searchPlaceholder: { type: String, required: false, default: 'Search in' },
      fieldId: {
        type: String,
        default: () => {
          const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = (Math.random() * 16) | 0,
              v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });

          return `search-field-${uuid}`;
        },
      },
      productListLabel: { type: String, required: false, default: 'Products' },
      articleListLabel: { type: String, required: false, default: 'Articles' },
      urlParam: { type: Boolean, required: false, default: false },
      numberResults: { type: String, required: false, default: '' },
    },
    data() {
      return {
        active: false,
        searchValue: '',
        showSuggestions: false,
      };
    },
    computed: {
      ...mapGetters('searchStore', [
        'suggested',
        'suggestedProducts',
        'suggestedClp',
        'suggestedArticles',
        'loading',
        'searchTerm',
      ]),
    },
    created() {
      this.throttleCheckTerm = throttle(this.checkTerm, 1000);
      if (this.urlParam) {
        this.searchValue = this.getQueryParameter();
      }
      this.onLoadPushTagging();
    },
    methods: {
      ...mapActions('searchStore', [
        'setSearchTerm',
        'emptySearchTerm',
        'submitSearch',
        'fetchSuggestedQueries',
      ]),
      submit() {
        this.setSearchTerm(this.searchValue);
        this.pushTagging();
        this.submitSearch();
      },
      clear() {
        this.searchValue = '';
        this.setSearchTerm(this.searchValue);
        this.hideSuggestions();
      },
      hideSuggestions() {
        this.showSuggestions = false;
      },
      checkTerm() {
        this.showIfItHasSuggestions();
        this.setSearchTerm(this.searchValue);
        this.fetchSuggestedQueries();
      },
      showIfItHasSuggestions() {
        this.showSuggestions = this.searchValue.length >= 3;
      },
      focusFirstSuggestion() {
        const firstItem = document.querySelector(`#list-${this.fieldId} > li > a`);

        if (firstItem) {
          firstItem.focus();
        }
      },
      pushTagging() {
        if (!this.searchTerm) {
          return;
        }
        const manualTagging = {
          event: 'uaevent',
          ecommerce: undefined,
          eventAction: 'search::manual',
          eventCategory: 'internal search',
          eventLabel: this.searchTerm,
          event_name: 'internal_search',
          method: 'manual',
          search_keyword: this.searchTerm,
        };
        AnalyticsHandler.pushDataLayer(manualTagging);
      },
      getQueryParameter(param = 'q') {
        const searchQuery =
          window.location.search && new URLSearchParams(window.location.search).get(param);
        return searchQuery ? searchQuery : '';
      },
      onLoadPushTagging() {
        AnalyticsHandler.pushDataLayer({
          event: 'uaevent',
          ecommerce: undefined,
          eventAction: 'search::manual',
          eventCategory: 'internal search',
          eventLabel: this.searchValue,
          event_name: 'internal_search',
          method: 'manual',
          search_keyword: this.searchValue,
          number_results: this.numberResults,
        });
      },
    },
  };
</script>
